import { useEffect, useState } from "react";
import { Copy, Trash2, UserPlus, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { staff as staffApi, type StaffMember, type StaffInviteRow, type ScopeGroup } from "../lib/api";

const ROLE_LABEL: Record<string, string> = {
  owner: "Owner", manager: "Manager", analyst: "Analyst", viewer: "Viewer",
};
// Owner is never invited (there's exactly one). The middle role — Manager —
// carries the permission checkboxes. Viewer is always read-only.
const INVITE_ROLES = [
  { value: "manager", label: "Manager — you choose what they can do" },
  { value: "viewer", label: "Viewer — read-only, can't change anything" },
];

/** The grouped permission checkboxes + the "Allow all" master switch. */
function PermissionPicker({
  catalog, allowAll, selected, disabled, onAllowAll, onToggle,
}: {
  catalog: ScopeGroup[];
  allowAll: boolean;
  selected: Set<string>;
  disabled?: boolean;
  onAllowAll: (v: boolean) => void;
  onToggle: (scope: string, on: boolean) => void;
}) {
  return (
    <div className="perm-picker" style={{ border: "1px solid var(--ne-line)", borderRadius: 10, padding: "12px 14px", background: "var(--ne-black-2)" }}>
      <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer", fontWeight: 700, marginBottom: allowAll ? 0 : 10 }}>
        <input type="checkbox" checked={allowAll} disabled={disabled}
          onChange={(e) => onAllowAll(e.target.checked)}
          style={{ width: 18, height: 18, accentColor: "var(--ne-red-bright)" }} />
        <span><ShieldCheck size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />Allow all permissions</span>
      </label>
      {!allowAll && (
        <div style={{ display: "grid", gap: 14, marginTop: 4 }}>
          {catalog.map((g) => (
            <div key={g.group}>
              <p className="muted small" style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 6px" }}>{g.group}</p>
              <div style={{ display: "grid", gap: 6 }}>
                {g.scopes.map((sc) => (
                  <label key={sc.scope} style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.has(sc.scope)} disabled={disabled}
                      onChange={(e) => onToggle(sc.scope, e.target.checked)}
                      style={{ marginTop: 2, width: 16, height: 16, accentColor: "var(--ne-red-bright)" }} />
                    <span className="small">
                      {sc.label}
                      {sc.note && <span className="muted small" style={{ marginLeft: 6, fontStyle: "italic" }}>· {sc.note}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StaffManager({ currentUserId }: { currentUserId?: string }) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [invites, setInvites] = useState<StaffInviteRow[]>([]);
  const [catalog, setCatalog] = useState<ScopeGroup[]>([]);
  const [cap, setCap] = useState(5);
  const [slotsLeft, setSlotsLeft] = useState(5);
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("manager");
  const [allowAll, setAllowAll] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);

  // Which existing staff member's permissions are being edited (id), if any.
  const [editing, setEditing] = useState<string | null>(null);
  const [editAllowAll, setEditAllowAll] = useState(true);
  const [editSelected, setEditSelected] = useState<Set<string>>(new Set());

  async function refresh() {
    const r = await staffApi.list();
    setStaff(r.staff); setInvites(r.pending_invites);
    setCatalog(r.permission_catalog || []);
    setCap(r.cap); setSlotsLeft(r.slots_left);
    setLoading(false);
  }
  useEffect(() => { refresh().catch((e: any) => { setError(e?.message || "Could not load staff"); setLoading(false); }); }, []);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setInviteLink(null); setEmailedTo(null);
    try {
      const r = await staffApi.invite({
        email: email.trim().toLowerCase(),
        role,
        name: name.trim() || undefined,
        allow_all: role === "viewer" ? undefined : allowAll,
        permissions: role === "manager" && !allowAll ? [...selected] : undefined,
      });
      if (r.email.mode !== "resend") setInviteLink(r.invite_url);
      else setEmailedTo(email.trim().toLowerCase());
      setEmail(""); setName(""); setAllowAll(true); setSelected(new Set());
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not send invite");
    } finally { setBusy(false); }
  }

  function startEdit(s: StaffMember) {
    setEditing(s.id);
    setEditAllowAll(s.allow_all);
    setEditSelected(new Set(s.effective_scopes || []));
    setError(null);
  }
  async function saveEdit(s: StaffMember) {
    setBusy(true); setError(null);
    try {
      await staffApi.updatePermissions(s.id, {
        allow_all: editAllowAll,
        permissions: editAllowAll ? undefined : [...editSelected],
      });
      setEditing(null);
      await refresh();
    } catch (e: any) { setError(e?.message || "Could not save permissions"); }
    finally { setBusy(false); }
  }

  async function removeStaff(id: string) {
    if (!confirm("Remove this staff login? They'll no longer be able to sign in.")) return;
    try { await staffApi.remove(id, "user"); await refresh(); }
    catch (e: any) { setError(e?.message || "Could not remove"); }
  }
  async function revokeInvite(id: string) {
    try { await staffApi.remove(id, "invite"); await refresh(); }
    catch (e: any) { setError(e?.message || "Could not revoke"); }
  }

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void) => (scope: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(scope); else next.delete(scope);
    setter(next);
  };

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Manage staff / admin</h2>
        <span className="muted small">{slotsLeft} of {cap} logins available</span>
      </div>

      {loading ? <p className="muted">Loading…</p> : (
        <>
          {/* Roster */}
          <div className="staff-list">
            {staff.map((s) => (
              <div key={s.id} className="staff-row" style={{ flexWrap: "wrap" }}>
                <div>
                  <strong>{s.name || s.email}</strong>
                  <span className="muted small"> · {s.email}</span>
                  {s.role !== "owner" && s.role !== "viewer" && (
                    <span className="muted small"> · {s.allow_all ? "full access" : `${s.effective_scopes.length} permissions`}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="pill">{ROLE_LABEL[s.role] || s.role}</span>
                  {s.role !== "owner" && s.role !== "viewer" && s.id !== currentUserId && (
                    <button className="icon-button" aria-label={`Edit permissions for ${s.name || s.email}`} onClick={() => (editing === s.id ? setEditing(null) : startEdit(s))}>
                      <SlidersHorizontal size={16} />
                    </button>
                  )}
                  {s.role !== "owner" && s.id !== currentUserId && (
                    <button className="icon-button" aria-label={`Remove ${s.name || s.email}`} onClick={() => removeStaff(s.id)}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {editing === s.id && (
                  <div style={{ flexBasis: "100%", marginTop: 10 }}>
                    <PermissionPicker catalog={catalog} allowAll={editAllowAll} selected={editSelected}
                      onAllowAll={setEditAllowAll} onToggle={toggle(editSelected, setEditSelected)} />
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button className="btn-primary" disabled={busy} onClick={() => saveEdit(s)}>{busy ? "Saving…" : "Save permissions"}</button>
                      <button className="ghost-button" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {invites.map((iv) => (
              <div key={iv.id} className="staff-row staff-row--pending">
                <div>
                  <strong>{iv.name || iv.email}</strong>
                  <span className="muted small"> · {iv.email} · invite pending</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="pill pill--muted">{ROLE_LABEL[iv.role] || iv.role}</span>
                  <button className="icon-button" aria-label={`Revoke invite for ${iv.email}`} onClick={() => revokeInvite(iv.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Invite */}
          {slotsLeft > 0 ? (
            <form onSubmit={invite} className="stacked-form" style={{ marginTop: 14 }}>
              <div className="form-row-2">
                <label className="form-field"><span>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="specialist@yourbusiness.com" required />
                </label>
                <label className="form-field"><span>Name (optional)</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" />
                </label>
              </div>
              <label className="form-field"><span>Role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
              {role === "manager" && (
                <div className="form-field">
                  <span>Permissions</span>
                  <PermissionPicker catalog={catalog} allowAll={allowAll} selected={selected}
                    onAllowAll={setAllowAll} onToggle={toggle(selected, setSelected)} />
                  <p className="muted small" style={{ marginTop: 6 }}>
                    Leave "Allow all permissions" on to give them full manager access, or turn it off and pick exactly what they can do.
                  </p>
                </div>
              )}
              {role === "viewer" && (
                <p className="muted small">A viewer can open and read everything but cannot change, send, pull, or charge anything.</p>
              )}
              {error && <p className="error-text" role="alert">{error}</p>}
              <button className="btn-primary" disabled={busy} style={{ alignSelf: "flex-start" }}>
                <UserPlus size={16} aria-hidden="true" /> {busy ? "Sending…" : "Send invite"}
              </button>
            </form>
          ) : (
            <p className="muted small" style={{ marginTop: 12 }}>
              You're at your {cap}-login limit. Remove a staff login or revoke a pending invite to add another.
            </p>
          )}

          {emailedTo && (
            <p className="muted small" style={{ marginTop: 10, color: "var(--ne-success)" }}>
              Invite emailed to {emailedTo}. They'll set their own password from the link.
            </p>
          )}
          {inviteLink && (
            <div className="staff-invite-link" style={{ marginTop: 10 }}>
              <p className="muted small" style={{ margin: "0 0 6px" }}>Email isn't configured yet — send them this invite link:</p>
              <div className="input-with-affix">
                <input value={inviteLink} readOnly onFocus={(e) => e.currentTarget.select()} />
                <button type="button" className="input-affix-button" aria-label="Copy invite link"
                  onClick={() => navigator.clipboard?.writeText(inviteLink)}>
                  <Copy size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
